import { Col, Form, Input, Row, Select, Switch } from "antd";
import Title from "antd/es/typography/Title";
import queryClient from "api/queryClient.ts";
import { usePatchScenarioMutation } from "api/TenantSlug/ConnectionCreator/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { ConnectionCreatorScenarioCategory } from "types/enums";
import { FormId } from "types/models.ts";
import { isStructureScenarioCategory } from "utils/scenarioFormStructure";

import { ScenarioGeneralFormData, schema } from "./formSchema";

import ScenarioData = App.Data.AppScenarioData;

interface ScenarioGeneralFormProps {
    scenario: ScenarioData;
}

interface ScenarioGeneralFormContentProps {
    scenario: ScenarioData;
}

const ScenarioGeneralFormContent: FC<ScenarioGeneralFormContentProps> = ({ scenario }) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const { control, fieldNames } = useGeneralForm<ScenarioGeneralFormData>();
    const showFormStructureSelector = isStructureScenarioCategory(scenario.category);

    const categoryOptions = useMemo(
        () =>
            Object.values(ConnectionCreatorScenarioCategory).map((cat) => ({
                value: cat,
                label: tCC(`enums.scenario_category.${cat.replace(/-/g, "_")}`),
            })),
        [tCC]
    );

    return (
        <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <Title level={3} className="mb-6">
                        {tC("general")}
                    </Title>
                    <Row gutter={16}>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.title.name}
                                label={tFields("name.label")}
                                required
                            >
                                <Input placeholder={tFields("name.placeholder")} />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label={tCC("enums.scenario_category.label")}>
                                <Select
                                    value={scenario.category as ConnectionCreatorScenarioCategory}
                                    options={categoryOptions}
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <FormItem
                                control={control}
                                name={fieldNames.is_enabled.name}
                                label={tFields("status.label")}
                                valuePropName="checked"
                            >
                                <Switch />
                            </FormItem>
                        </Col>
                    </Row>
                    {showFormStructureSelector && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <FormItem
                                    control={control}
                                    name="form_structure_ids"
                                    label={tFields("form_structure.label")}
                                >
                                    <FormStructureSelectSearchable
                                        mode="multiple"
                                        allowClear
                                        placeholder={tFields("form_structure.placeholder")}
                                    />
                                </FormItem>
                            </Col>
                        </Row>
                    )}
                </SectionContentWrapper>
            </div>
        </div>
    );
};

export const ScenarioGeneralForm: FC<ScenarioGeneralFormProps> = ({ scenario }) => {
    const { t } = usePaldockTranslation("forms.scenario-general");
    const patchScenarioMutation = usePatchScenarioMutation(scenario.id);

    const initialFormStructureIds = scenario.form_structures
        ?.map((formStructure) => formStructure.id ?? "")
        .filter(Boolean);

    const handleSubmit = (values: ScenarioGeneralFormData) => {
        patchScenarioMutation.mutate(
            {
                id: scenario.id,
                title: values.title,
                is_enabled: values.is_enabled,
                category: scenario.category as ConnectionCreatorScenarioCategory,
                ...(isStructureScenarioCategory(scenario.category) && initialFormStructureIds !== undefined
                    ? { form_structure_ids: values.form_structure_ids ?? [] }
                    : {}),
            },
            {
                onSuccess: () => {
                    toast.success(t("messages.update_success"));
                    queryClient.refetchQueries({ queryKey: ["getScenario", scenario.id] });
                },
                onError: () => {
                    toast.error(t("messages.update_error"));
                },
            }
        );
    };

    const defaultValues: ScenarioGeneralFormData = {
        title: scenario.title || "",
        is_enabled: scenario.is_enabled ?? true,
        form_structure_ids: initialFormStructureIds ?? [],
    };

    return (
        <GeneralFormProvider<ScenarioGeneralFormData>
            key={scenario.id}
            onSubmit={handleSubmit}
            id={FormId.ScenarioGeneralForm}
            schema={schema}
            defaultValues={defaultValues}
            resetOnSubmit={false}
        >
            <ScenarioGeneralFormContent scenario={scenario} />
        </GeneralFormProvider>
    );
};
